import {
    useCallback, useEffect, useRef, useState,
} from 'react';

interface UseDictateOptions {
    onAutoAccept?: (blob: Blob) => void;
    silenceThreshold?: number; // in milliseconds (default: 2 minutes)
    maxDuration?: number; // in milliseconds (default: 5 minutes)
}

interface UseDictateReturn {
    isRecording: boolean;
    audioBlob: Blob | null;
    stream: MediaStream | null;
    startRecording: () => Promise<void>;
    stopRecording: () => void;
    cancelRecording: () => void;
    error: string | null;
}

const useDictate = (options: UseDictateOptions = {}): UseDictateReturn => {
    const {
        onAutoAccept,
        silenceThreshold = 120000, // 2 minutes
        maxDuration = 300000, // 5 minutes
    } = options;

    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [error, setError] = useState<string | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const silenceStartTimeRef = useRef<number | null>(null);
    const recordingStartTimeRef = useRef<number | null>(null);
    const checkIntervalRef = useRef<number | null>(null);
    const shouldAutoAcceptRef = useRef<boolean>(false);

    const startRecording = useCallback(async () => {
        try {
            setError(null);
            const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = mediaStream;
            setStream(mediaStream);

            // Create audio context for silence detection
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaStreamSource(mediaStream);

            analyser.fftSize = 2048;
            analyser.smoothingTimeConstant = 0.3;
            source.connect(analyser);

            audioContextRef.current = audioContext;
            analyserRef.current = analyser;

            const mediaRecorder = new MediaRecorder(mediaStream, {
                mimeType: 'audio/webm;codecs=opus',
            });
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];
            recordingStartTimeRef.current = Date.now();
            silenceStartTimeRef.current = null;
            shouldAutoAcceptRef.current = false;

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob);

                // Stop all tracks
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((track) => track.stop());
                }
                setStream(null);

                // Cleanup audio context
                if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                    try {
                        await audioContextRef.current.close();
                    } catch {
                        // Ignore errors when closing
                    }
                    audioContextRef.current = null;
                }

                // Clear interval if still running
                if (checkIntervalRef.current) {
                    clearInterval(checkIntervalRef.current);
                    checkIntervalRef.current = null;
                }

                // Trigger auto-accept only if stopRecording was called (not cancelRecording)
                if (shouldAutoAcceptRef.current && onAutoAccept && blob.size > 0) {
                    setTimeout(() => {
                        onAutoAccept(blob);
                    }, 100);
                }
            };

            mediaRecorder.start();
            setIsRecording(true);

            // Start checking for silence and max duration
            const checkSilenceAndDuration = () => {
                if (!analyserRef.current || !mediaRecorderRef.current) {
                    if (checkIntervalRef.current) {
                        clearInterval(checkIntervalRef.current);
                        checkIntervalRef.current = null;
                    }
                    return;
                }

                const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
                analyserRef.current.getByteTimeDomainData(dataArray);

                // Calculate RMS (Root Mean Square) for volume detection
                let sumSquares = 0;
                for (let i = 0; i < dataArray.length; i += 1) {
                    const normalized = (dataArray[i] - 128) / 128;
                    sumSquares += normalized * normalized;
                }
                const rms = Math.sqrt(sumSquares / dataArray.length);
                const volumeThreshold = 0.01; // Adjust this value based on your needs (0.0 to 1.0)

                const now = Date.now();
                const recordingDuration = now - (recordingStartTimeRef.current || now);

                // Check for silence
                if (rms < volumeThreshold) {
                    if (silenceStartTimeRef.current === null) {
                        silenceStartTimeRef.current = now;
                    } else {
                        const silenceDuration = now - silenceStartTimeRef.current;
                        if (silenceDuration >= silenceThreshold) {
                            // 2 minutes of silence - stop and accept
                            if (checkIntervalRef.current) {
                                clearInterval(checkIntervalRef.current);
                                checkIntervalRef.current = null;
                            }
                            mediaRecorderRef.current.stop();
                            setIsRecording(false);
                            return;
                        }
                    }
                } else {
                    // Sound detected - reset silence timer
                    silenceStartTimeRef.current = null;
                }

                // Check for max duration (5 minutes)
                if (recordingDuration >= maxDuration) {
                    if (checkIntervalRef.current) {
                        clearInterval(checkIntervalRef.current);
                        checkIntervalRef.current = null;
                    }
                    mediaRecorderRef.current.stop();
                    setIsRecording(false);
                }
            };

            // Check every 500ms
            checkIntervalRef.current = window.setInterval(checkSilenceAndDuration, 500);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
            setError(errorMessage);
            setIsRecording(false);
            setStream(null);
        }
    }, [silenceThreshold, maxDuration, onAutoAccept]);

    const stopRecording = useCallback(() => {
        if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
        }
        if (mediaRecorderRef.current && isRecording) {
            shouldAutoAcceptRef.current = true;
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    }, [isRecording]);

    const cancelRecording = useCallback(() => {
        if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
        }
        if (mediaRecorderRef.current && isRecording) {
            shouldAutoAcceptRef.current = false;
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }

        // Stop all tracks
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
        }
        setStream(null);

        // Clean up audio context
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            (async () => {
                try {
                    await audioContextRef.current!.close();
                } catch {
                    // Ignore errors when closing
                }
            })();
            audioContextRef.current = null;
        }

        // Clean up
        setAudioBlob(null);
        chunksRef.current = [];
        silenceStartTimeRef.current = null;
        recordingStartTimeRef.current = null;
    }, [isRecording]);

    // Cleanup on unmount
    useEffect(() => () => {
        if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            (async () => {
                try {
                    await audioContextRef.current!.close();
                } catch {
                    // Ignore errors when closing
                }
            })();
            audioContextRef.current = null;
        }
    }, []);

    return {
        isRecording,
        audioBlob,
        stream,
        startRecording,
        stopRecording,
        cancelRecording,
        error,
    };
};

export default useDictate;
